import type { SetupUrgency, SetupWhere, SetupWho } from '@norum/contracts';
import { denialFor } from '@/api/denials';
import type {
  WireHostedClient,
  WireLiveTaskGrant,
  WireNotifyChannel,
  WirePendingTaskTool,
  WireReadinessCheck,
  WireReadinessFeature,
  WireSetupStep,
  WireTaskGrantCount,
  WireVendor,
  WireVendorUnit,
} from '@/api/wire';
import { toolName } from '@/app/technical';
import type { ItemContext, Party } from '@/ui/context';
import { twinLabel } from '@/ui/people';
import { dayWords, humanise } from '@/ui/words';
import type { Who } from '@/ui/who';
import { clean, listWords, plural, waitedWords } from './explain';

/**
 * The superadmin's own screens, said as a record says itself (ADR-0155):
 * setup steps and readiness checks, vendor plans and their lines, what the
 * reports count, the stop switch, and the tools a twin's task waits on.
 * Pure builders over rows the screen already holds. Everything a figure
 * says is computed from the figures shown beside it; no benchmark is
 * invented, and no id, code or address reaches a sentence.
 */

export const WHO_LABEL: Record<SetupWho, string> = {
  superadmin: 'A superadmin',
  'workspace-admin': 'A Google Workspace admin',
  'project-owner': 'The Cloud project owner',
  'repo-admin': 'A GitHub repository admin',
  signer: 'Whoever holds the signing key',
  engineer: 'An engineer',
  lead: "The department's lead (or a superadmin)",
  'any-person': 'Anyone',
  'vendor-account-holder': "The vendor account's holder",
  'aws-account-holder': 'The AWS account holder',
  counsel: 'Counsel',
};

export const WHERE_LABEL: Record<SetupWhere, string> = {
  platform: 'Here, in the platform',
  'google-admin': 'Google Admin console',
  'gcp-console': 'Google Cloud console',
  'cloud-shell': 'Cloud Shell, or a terminal on your machine',
  terminal: 'A terminal: Cloud Shell or your machine',
  github: 'GitHub',
  cloudflare: 'Cloudflare',
  vendor: "The vendor's own site",
  aws: 'AWS console',
  counsel: 'Counsel',
};

export const URGENCY_LABEL: Record<SetupUrgency, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

const usd = (n: number): string => `$${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const count = (n: number): string => (Number.isInteger(n) ? n.toLocaleString('en-GB') : n.toLocaleString('en-GB', { maximumFractionDigits: 2 }));
const platform = (when?: string): Party & { when?: string | undefined } => ({ name: 'The platform', kind: 'system', ...(when ? { when } : {}) });
const asPerson = (who: Who, id: string | null | undefined): Party | null => who.actor(id);

// ---------------------------------------------------------------------------
// The setup roadmap
// ---------------------------------------------------------------------------

export function explainSetupStep(s: WireSetupStep, who: Who, extra: { phaseTitle?: string | undefined; byId?: ReadonlyMap<string, { title: string }> | undefined } = {}): ItemContext {
  const places = [...new Set(s.instructions.map((i) => WHERE_LABEL[i.where]))];
  const doer = WHO_LABEL[s.who];
  const mine = s.who === 'superadmin';
  const seen = s.recurring
    ? 'It is never done once.'
    : s.checks.length > 0
      ? 'The platform checks it itself and turns it done; nothing is marked by hand.'
      : 'No check can see it, so a superadmin says when it is done.';
  const first = s.waitingOn.map((id) => extra.byId?.get(id)?.title ?? 'another step');
  const next: ItemContext['next'] = s.recurring
    ? { tip: 'Nothing is needed now. It comes back on its own; act when the Now tab lists it as due.', instead: 'It is never counted as left, so skipping a round costs nothing but what it protects.' }
    : s.state === 'done'
      ? {
          tip: 'Nothing to do. It is done.',
          instead: s.checks.length > 0 ? 'If it stops being true, the platform’s check turns it back to “to do” by itself.' : 'If it stops being true, mark it not done at the bottom so the roadmap is honest.',
        }
      : s.state === 'blocked'
        ? { tip: `Do ${listWords(first)} first; this one cannot be done before it.`, instead: 'Nothing is lost by waiting: the step stays here and comes up once the one before it is done.' }
        : s.state === 'waiting'
          ? { tip: 'Nothing is missing on your side. The platform has not yet seen it work, and it looks again on its own after the next run.', instead: 'If it still reads this way after a day, use the numbered steps below to check each part.' }
          : s.state === 'unknown'
            ? { tip: 'The platform could not ask just now, so it claims nothing. Try again in a minute.', instead: 'If it stays unknown, the Readiness page says which check could not be read.' }
            : s.optional
              ? { tip: 'Only needed if you want what it unlocks, said below. Skip it otherwise.', instead: 'Skipping it is fine; it is never counted as left.' }
              : {
                  tip: `${s.urgency === 'critical' ? 'This is critical: what it unlocks does not work without it. ' : ''}${mine ? 'It is yours to do' : `${doer} does this`}. Work through the numbered steps below, in order, in ${listWords(places)}.`,
                  instead: s.checks.length > 0 ? 'You do not mark it done: the platform turns it done when its own check passes.' : 'Once you have done it, mark it done at the bottom with a note.',
                };
  return {
    subject: { kind: 'setup step', title: s.title },
    summary: s.what,
    need: s.state === 'done' || s.recurring ? undefined : `${doer} must do it, in ${listWords(places)}.`,
    why: s.why,
    source: { label: `The setup roadmap${extra.phaseTitle ? `, “${extra.phaseTitle}”` : ''}`, when: dayWords(s.added) },
    broughtBy: platform(dayWords(s.added)),
    owner: mine ? { ...who.me, role: 'does this step' } : { name: doer, kind: 'person' },
    facts: [
      ['Urgency', URGENCY_LABEL[s.urgency]],
      ['Unlocks', s.unlocks],
      ['Where', listWords(places)],
      ['How it is seen done', seen],
    ],
    next,
  };
}

export function explainCheck(c: WireReadinessCheck, extra: { feature: string; step?: { id: string; title: string; who: SetupWho } | undefined; generatedAt?: string | undefined }, who: Who): ItemContext {
  const doer = extra.step ? WHO_LABEL[extra.step.who] : 'A superadmin';
  const next: ItemContext['next'] =
    c.state === 'done'
      ? { tip: 'Nothing to do. The platform has seen this working.', instead: 'It is re-read on every pass, so if it breaks it will say so here.' }
      : c.state === 'waiting'
        ? { tip: 'Nothing is missing on your side. The platform has not yet seen it happen, and looks again on its own.', instead: 'If it still waits after a day, open the step it belongs to and check each part.' }
        : c.state === 'unknown'
          ? { tip: 'The platform could not ask just now, so it claims nothing. Reload in a minute.', instead: 'If it stays unknown, the service that answers it may be down: say so to an engineer.' }
          : extra.step
            ? {
                tip: `Something is missing. Follow “${extra.step.title}” on the Workspace: it lists who does it, where, and the exact commands, and this check turns on by itself once it is done.`,
                instead: 'The line above says what the platform found; fixing that is what turns it on.',
                actions: [{ label: 'Open the step', to: `/workspace?step=${extra.step.id}`, primary: true }],
              }
            : { tip: 'Something is missing and no setup step covers it yet. The line above says what; tell Norum Tech so a step is written for it.', instead: 'Nothing else turns it on.' };
  return {
    subject: { kind: 'check', title: c.title },
    summary: `The platform checked “${c.title}” on itself, as part of “${extra.feature}”.`,
    need: c.state === 'todo' ? `${doer} must put right what the line above says.` : undefined,
    why: 'Readiness reads the platform’s own state and never the runbook, so it says what is true now.',
    source: { label: 'The platform’s own Readiness report', ...(extra.generatedAt ? { when: dayWords(extra.generatedAt) } : {}) },
    broughtBy: platform(extra.generatedAt ? dayWords(extra.generatedAt) : undefined),
    owner: c.state === 'todo' ? (doer === 'A superadmin' ? { ...who.me, role: 'puts it right' } : { name: doer, kind: 'person' }) : undefined,
    next,
    ...(extra.step ? { open: { to: `/workspace?step=${extra.step.id}`, label: 'Open the step' } } : {}),
  };
}

export function explainFeature(f: WireReadinessFeature, who: Who): ItemContext {
  const total = f.checks.length;
  const on = f.checks.filter((c) => c.state === 'done').length;
  const todo = f.checks.filter((c) => c.state === 'todo').length;
  const waiting = f.checks.filter((c) => c.state === 'waiting').length;
  const unknown = f.checks.filter((c) => c.state === 'unknown').length;
  return {
    subject: { kind: 'feature', title: f.title },
    summary: `${f.title}: ${on} of ${plural(total, 'check')} on${todo > 0 ? `, ${todo} with something for a person to do` : ''}${waiting > 0 ? `, ${waiting} waiting to be seen working` : ''}${unknown > 0 ? `, ${unknown} not known` : ''}.`,
    need: todo > 0 ? `A person must do what the ${todo === 1 ? 'check' : 'checks'} marked “to do” below ${todo === 1 ? 'says' : 'say'}.` : undefined,
    why: 'Every feature is listed with what proves it works, so nothing is assumed to be on.',
    source: { label: 'The platform’s own Readiness report' },
    broughtBy: platform(),
    owner: todo > 0 ? { ...who.me, role: 'decides who does it' } : undefined,
    next:
      f.state === 'done'
        ? { tip: 'Nothing is needed. Every check of it is on.', instead: 'It is read again on every pass.' }
        : todo > 0
          ? { tip: 'Start with the first check marked “to do” below; each links to the step that lists the commands.', instead: 'A check waiting to be seen needs nothing from you.' }
          : { tip: 'Nothing is missing on your side. What remains is waiting to be seen working, and the platform looks again on its own.', instead: 'If it stays this way for a day, open its checks and see which has not run.' },
  };
}

// ---------------------------------------------------------------------------
// The Workspace's own summary
// ---------------------------------------------------------------------------

export function explainWorkspaceSummary(n: { decide: number; stepsLeft: number; stepsOf: number; critical: number; proposals: number; fresh: number }): ItemContext {
  const tip =
    n.critical > 0
      ? `${plural(n.critical, 'setup step')} ${n.critical === 1 ? 'is' : 'are'} critical: start there, in the Setup roadmap tab, since what they unlock does not work without them.`
      : n.decide > 0
        ? `${plural(n.decide, 'thing')} wait${n.decide === 1 ? 's' : ''} on a decision. Open the Now tab and take the oldest first.`
        : n.stepsLeft > 0
          ? `Nothing waits on a decision. ${plural(n.stepsLeft, 'setup step')} ${n.stepsLeft === 1 ? 'is' : 'are'} left; the most urgent comes first in the Setup roadmap tab.`
          : 'Nothing is outstanding. The platform will say here when something is.';
  return {
    subject: { kind: 'summary', title: 'The Workspace’s four counts' },
    summary: `${n.decide} to decide or watch, ${n.stepsLeft} of ${n.stepsOf} setup steps left, ${n.proposals} proposals and ${n.fresh} that arrived since you last looked.`,
    need: n.decide + n.stepsLeft > 0 ? 'A superadmin: these wait on you.' : undefined,
    why: 'The Workspace is the one place a superadmin reads what is outstanding. Each count is a tab; each item in it opens where it is decided.',
    source: { label: 'Counted from the platform’s own queues and the setup catalogue' },
    broughtBy: platform(),
    next: { tip, instead: 'What is new is kept in this browser only, so another browser starts from last week.' },
  };
}

// ---------------------------------------------------------------------------
// Vendor plans and their lines
// ---------------------------------------------------------------------------

export function explainVendorUnit(v: WireVendor, u: WireVendorUnit, who: Who): ItemContext {
  const period = u.period === 'plan' ? 'for the whole plan' : `this ${u.period}`;
  const used = `${count(u.used)} ${u.label} ${u.period === 'plan' ? 'so far' : period}`;
  const pct = u.stop > 0 ? Math.round((u.used / u.stop) * 100) : null;
  const passes = !u.limitOnly && !u.deploymentBudget && u.periodEnd !== null && u.forecast > u.stop && u.state !== 'stopped';
  const decider = u.decidedBy ? asPerson(who, u.decidedBy) : null;
  const tipState: Record<WireVendorUnit['state'], { tip: string; instead: string }> = {
    free: {
      tip: passes ? `Within what is free for now (${pct}% of the line), but at this pace it reaches ${count(u.forecast)} by the end, past the line at ${count(u.stop)}. Decide now whether that use is wanted.` : `Nothing is needed. ${pct !== null ? `It has used ${pct}% of the way to its line.` : 'It is within what is free.'}`,
      instead: 'If you ever want to allow more, use “Change the line” on this card; raising it spends a billing code, lowering it needs none.',
    },
    near_limit: {
      tip: `It is near its line (${count(u.used)} of ${count(u.stop)}). If this use is wanted, raise the line with “Change the line” on this card; if not, leave it.`,
      instead: 'Leaving it is safe: at the line the gateway refuses the call before anything is spent.',
    },
    stopped: {
      tip: u.periodEnd ? `It has reached its line, so the gateway refuses it until the period turns on ${dayWords(u.periodEnd)}. Nothing is spent past it.` : 'It has reached its line, so the gateway refuses it. Only a raised line lets it run again.',
      instead: u.limitOnly ? 'This is the vendor’s own limit, so nothing can be bought past it.' : 'To let it run sooner, raise the line with “Change the line” on this card, which spends a billing code.',
    },
    paid: {
      tip: `Paid use that a superadmin allowed is being used${u.projectedCostUsd !== null ? `; at this pace it would cost ${usd(u.projectedCostUsd)}` : ''}. Check that is what you meant to allow.`,
      instead: 'To stop paying, lower the allowed paid use with “Change the line”; lowering needs no code.',
    },
    paused: { tip: `${v.name} is paused, so nothing reaches it. Resume it from the top of its card once the reason is settled.`, instead: 'Resuming spends a billing code; leaving it paused costs nothing.' },
    limit: { tip: 'Nothing is needed. This is a limit the vendor sets, not a price; it stops at its line and nothing can be bought past it.', instead: 'The line itself can be lowered if you want more of a margin.' },
  };
  return {
    subject: { kind: 'vendor line', title: `${v.name}: ${u.label}` },
    summary: `${v.name} gives ${u.deploymentBudget ? 'nothing free for this' : `${count(u.included)} ${u.label} free ${u.period === 'plan' ? 'for the whole plan' : `a ${u.period}`}`}. ${used.charAt(0).toUpperCase()}${used.slice(1)}, and the line where the gateway stops it is ${count(u.stop)}.`,
    need: u.state === 'near_limit' || u.state === 'stopped' || passes ? 'A decision: raise the line, or let it hold.' : undefined,
    why: 'Nothing paid is used unless a superadmin allows it, so every vendor’s use is counted against what is free.',
    source: { label: `The vendor’s own published figures, read ${dayWords(v.verifiedOn)}${u.recorded ? ', then a line a superadmin recorded' : ''}` },
    broughtBy: decider && u.decidedAt ? { ...decider, role: 'set the line', when: dayWords(u.decidedAt) } : null,
    owner: { ...who.me, role: 'sets the line' },
    facts: [
      ['Counted by', listWords(Object.keys(u.bySource).map((k) => humanise(k).toLowerCase()))],
      ['At this pace', u.periodEnd && !u.limitOnly ? `${count(u.forecast)} by ${dayWords(u.periodEnd)}` : null],
      ['Would cost', u.projectedCostUsd !== null ? usd(u.projectedCostUsd) : null],
    ],
    next: tipState[u.state],
  };
}

export function explainVendor(v: WireVendor, who: Who): ItemContext {
  const stopped = v.units.filter((u) => u.state === 'stopped');
  const near = v.units.filter((u) => u.state === 'near_limit');
  const pause = v.pause;
  const pauser = pause ? asPerson(who, pause.by) : null;
  const planEnded = v.plan.ended;
  const tip = pause
    ? 'It is paused, so every call is refused. Resume it from its card once the reason is settled, which spends a billing code.'
    : planEnded
      ? 'Its plan has ended, so nothing it gave free is free any more. Record the new plan with “Record the plan”, which spends a billing code.'
      : stopped.length > 0
        ? `${listWords(stopped.map((u) => u.label))} ${stopped.length === 1 ? 'has' : 'have'} reached ${stopped.length === 1 ? 'its' : 'their'} line and ${stopped.length === 1 ? 'is' : 'are'} refused. Raise a line from its row only if that use is wanted.`
        : near.length > 0
          ? `${listWords(near.map((u) => u.label))} ${near.length === 1 ? 'is' : 'are'} near ${near.length === 1 ? 'its' : 'their'} line. Open its row to see the pace before deciding.`
          : 'Nothing is needed. Everything it gives is being used inside what is free.';
  return {
    subject: { kind: 'vendor', title: v.name },
    summary: `${v.name} is a vendor whose use is counted against ${v.plan.recorded ? `its ${v.plan.planName} plan` : 'the figures the catalogue holds for it, since its plan is not recorded yet'}${v.plan.endsOn ? `, which runs to ${dayWords(v.plan.endsOn)}` : ''}. ${plural(v.units.length, 'thing')} ${v.units.length === 1 ? 'is' : 'are'} counted.`,
    need: pause ? 'Resume it, or leave it paused.' : planEnded ? 'The new plan, recorded.' : undefined,
    why: 'Plans and usage is where a superadmin decides what each vendor may cost.',
    source: { label: `The vendor’s own pages, read ${dayWords(v.verifiedOn)}` },
    broughtBy: pause && pauser ? { ...pauser, role: 'paused it', when: dayWords(pause.at) } : null,
    owner: { ...who.me, role: 'decides what it may cost' },
    facts: [
      ['Paused', pause ? `${clean(pause.reason)}` : null],
      ['Plan', v.plan.recorded ? v.plan.planName : null],
    ],
    next: { tip, instead: pause ? 'Leaving it paused costs nothing, but every agent that needs it is refused.' : 'The cost lines below show what each unit has used.' },
  };
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export interface SpendRow {
  department: string;
  spentUsd: number;
  shipped: number;
  perChangeUsd: number | null;
}

export function explainSpendSummary(n: { days: number; totalUsd: number; calls: number; shipped: number; working: number | null; byDepartment: readonly SpendRow[] }, who: Who): ItemContext {
  const silent = n.byDepartment.filter((d) => d.spentUsd > 0 && d.shipped === 0);
  const dearest = [...n.byDepartment].filter((d) => d.perChangeUsd !== null).sort((a, b) => (b.perChangeUsd ?? 0) - (a.perChangeUsd ?? 0))[0];
  const tip =
    n.totalUsd === 0
      ? 'Nothing was spent in this window, so there is nothing to look at. Pick a longer window to see more.'
      : silent.length > 0
        ? `${listWords(silent.map((d) => who.label(d.department)))} spent money and shipped nothing in these ${n.days} days. Ask ${silent.length === 1 ? 'its lead' : 'their leads'} what it was for: it may be preparation rather than waste.`
        : dearest && n.byDepartment.length > 1
          ? `Every department that spent also shipped, so nothing stands out. ${who.label(dearest.department)} cost the most for each change that landed (${usd(dearest.perChangeUsd ?? 0)}), so start there if you want to look.`
          : 'Every department that spent also shipped, so nothing stands out.';
  return {
    subject: { kind: 'report', title: `Spend over ${n.days} days` },
    summary: `In the last ${n.days} days the platform spent ${usd(n.totalUsd)} on ${plural(n.calls, 'call')}, shipped ${plural(n.shipped, 'change')}${n.shipped > 0 ? `, so each change that landed cost ${usd(n.totalUsd / n.shipped)}` : ', so there is no cost per change yet'}${n.working !== null ? `, and ${plural(n.working, 'agent')} ${n.working === 1 ? 'is' : 'are'} working` : ''}.`,
    need: silent.length > 0 ? 'A conversation with the lead of each department that spent without shipping.' : undefined,
    why: 'The first question of an oversight page is whether the money is buying anything, so spend is read against what shipped.',
    source: { label: 'The cost ledger and the change pipeline, counted and never estimated' },
    broughtBy: platform(),
    knows: silent.flatMap((d) => who.knowers(d.department)).slice(0, 4),
    facts: [
      ['Spent', usd(n.totalUsd)],
      ['Model calls', count(n.calls)],
      ['Changes shipped', String(n.shipped)],
      ['Per change landed', n.shipped > 0 ? usd(n.totalUsd / n.shipped) : null],
    ],
    next: { tip, instead: 'The rows below split the same spend by department, region and model.' },
  };
}

export function explainDepartmentSpend(d: SpendRow, days: number, who: Who): ItemContext {
  const name = who.label(d.department);
  const tip =
    d.spentUsd === 0
      ? `${name} spent nothing in these ${days} days. Nothing is needed.`
      : d.shipped === 0
        ? `${name} spent ${usd(d.spentUsd)} and shipped nothing. Ask its lead what the money was for; a department that spends and does not ship is worth a conversation.`
        : `${name} spent ${usd(d.spentUsd)} and shipped ${plural(d.shipped, 'change')}, ${usd(d.perChangeUsd ?? d.spentUsd / d.shipped)} each. Nothing is needed unless that cost is more than you expected.`;
  return {
    subject: { kind: 'department’s spend', title: name },
    summary: `${name} spent ${usd(d.spentUsd)} on models in the last ${days} days and shipped ${plural(d.shipped, 'change')}.`,
    need: d.shipped === 0 && d.spentUsd > 0 ? 'An answer from its lead on what the spend was for.' : undefined,
    why: 'The report lists each department’s spend beside what it shipped.',
    source: { label: 'The cost ledger and the change pipeline' },
    broughtBy: platform(),
    owner: who.lead(d.department),
    involved: who.agentsOf(d.department),
    knows: who.knowers(d.department),
    next: { tip, instead: d.spentUsd > 0 ? 'Its own page shows the evidence for how freely it may act.' : undefined },
  };
}

export function explainGeography(rows: ReadonlyArray<{ geography: string; spentUsd: number; calls: number }>, days: number): ItemContext {
  const total = rows.reduce((n, r) => n + r.spentUsd, 0);
  const out = rows.filter((r) => r.geography === 'global');
  const outside = out.reduce((n, r) => n + r.spentUsd, 0);
  const outCalls = out.reduce((n, r) => n + r.calls, 0);
  return {
    subject: { kind: 'report', title: 'Where the thinking happened' },
    summary: `Of ${usd(total)} spent on models in ${days} days, ${total > 0 ? Math.round((outside / total) * 100) : 0}% went to a provider outside the EU (${plural(outCalls, 'call')}).`,
    why: 'What a model is sent is processed where the provider runs, so the region of every call is recorded.',
    source: { label: 'The cost ledger, by the region each call was made in' },
    broughtBy: platform(),
    next:
      outside > 0
        ? { tip: 'Some of it left the EU. If that is intended nothing is needed; if it is not, find which model it was in the list below and tell Norum Tech.', instead: 'A vendor that processes outside the EU is admitted only once its paperwork is marked on the Workspace.' }
        : { tip: 'Nothing is needed. Everything counted stayed in the EU or on the machine.', instead: undefined },
  };
}

export interface ProspectingFigures {
  days: number;
  spentEur: number;
  qualified: number;
  started: number;
  replied: number;
  refusedToday: number | null;
  refusedWindow: number;
  lookupsSaved: number;
}

export function explainProspectingTiles(n: ProspectingFigures, eur: (v: number) => string): ItemContext {
  const costPer = n.qualified > 0 ? n.spentEur / n.qualified : null;
  const rate = n.started > 0 ? n.replied / n.started : null;
  const tip =
    n.refusedToday !== null && n.refusedToday > 0
      ? `${plural(n.refusedToday, 'call')} ${n.refusedToday === 1 ? 'was' : 'were'} refused today. The tool table below says which tool and why; a ceiling reached resolves itself tomorrow, a missing grant is the lead’s to ask for.`
      : n.started === 0
        ? 'No first email has started a cadence in this window, so there is no reply rate yet. Nothing is wrong; there is simply nothing to measure.'
        : 'Nothing is refused today. Compare the cost per qualified lead with the reply rate: if you pay more for each lead and fewer reply, the plan’s target is worth a look.';
  return {
    subject: { kind: 'report', title: `Prospecting, ${n.days} days` },
    summary: `Over ${n.days} days the plans spent ${eur(n.spentEur)} and found ${plural(n.qualified, 'lead')} above the bar${costPer !== null ? `, ${eur(costPer)} each` : ''}. ${n.started > 0 ? `${n.replied} of ${n.started} first emails that reached someone were answered (${Math.round((rate ?? 0) * 100)}%).` : 'No first email has gone out yet.'} Our own index saved ${plural(n.lookupsSaved, 'lookup')}.`,
    why: 'Prospecting spends real money at vendors, so what it cost is read beside what it found.',
    source: { label: 'Counted from the plans, the cadences and the gateway’s own counters' },
    broughtBy: platform(),
    next: { tip, instead: 'Each figure is counted from the rows below it and never typed.' },
  };
}

export function explainReportTool(
  t: { toolId: string; calls: number; refused: number; ceilingHits: number; byReason: Record<string, number>; vendorCostMonthUsd: number | null },
  due: { due: boolean | null; rank: number | null } | undefined,
): ItemContext {
  const name = toolName(t.toolId);
  const reasons = listWords(Object.entries(t.byReason).map(([k, n]) => `${denialFor(k).title.toLowerCase()} (${n})`));
  return {
    subject: { kind: 'tool', title: name },
    summary: `${name} was called ${plural(t.calls, 'time')} and refused ${plural(t.refused, 'time')}${t.ceilingHits > 0 ? `, ${plural(t.ceilingHits, 'time')} of them at its daily ceiling` : ''}${reasons ? `. Refused for: ${reasons}` : ''}.`,
    need: t.refused > 0 ? 'The reason it was refused put right, if the calls were wanted.' : undefined,
    why: 'Every tool call goes through the gateway, which counts what it let through and what it refused.',
    source: { label: 'The gateway’s own counters, from the audit chain' },
    broughtBy: platform(),
    next:
      t.refused === 0
        ? { tip: 'Nothing is needed. Every call was let through.', instead: due?.due ? 'It is due for replacement by our own on cost; ask Norum Tech from the chat.' : undefined }
        : t.ceilingHits > 0
          ? { tip: 'Some calls hit the daily ceiling, which is the platform’s pace and resets by itself. Nothing is broken; the plan may need to spread its calls out.', instead: 'A ceiling is not raised to get past it.' }
          : { tip: 'Some calls were refused for the reasons named. A missing grant is the lead’s to ask for; a refusal for a rule is working as intended.', instead: 'The Refusals page says in words what each reason means and who can move it.' },
  };
}

// ---------------------------------------------------------------------------
// Stopping
// ---------------------------------------------------------------------------

export function explainKillSwitch(n: { engaged: boolean; running: number; total: number; updatedBy: string; updatedAt: string }, who: Who): ItemContext {
  const by = asPerson(who, n.updatedBy);
  return {
    subject: { kind: 'switch', title: 'Stop everything' },
    summary: n.engaged
      ? 'Every agent in every department is stopped. Nothing is deleted and no approval is lost; they simply cannot act.'
      : `${n.running} of ${plural(n.total, 'agent')} ${n.running === 1 ? 'is' : 'are'} working. One action here stops every one of them mid-flight, in every department, twins included.`,
    need: n.engaged ? 'A superadmin must start everything again once the reason is settled.' : undefined,
    why: 'This is the one control that does not depend on any department: it exists for the day something must stop at once.',
    source: { label: 'The platform-wide switch', when: dayWords(n.updatedAt) },
    broughtBy: by ? { ...by, role: 'last changed it', when: dayWords(n.updatedAt) } : null,
    owner: { ...who.me, role: 'the only one who may change it' },
    next: n.engaged
      ? { tip: 'Start everything again from this card once the reason it was stopped is settled. Each agent gets back exactly the permissions it had.', instead: 'Leaving it stopped is safe but nothing works; to stop only one department, start everything again and stop that department.' }
      : { tip: 'Nothing is needed. Use it only if something must stop everywhere at once; to stop one department or one agent, use the narrower controls instead.', instead: 'Stopping one department leaves every other running; both need a single-use code.' },
  };
}

export function explainDepartmentStop(n: { displayName: string; name: string; stopped: boolean; suspendedBy: string | null; reason: string | null; agentsHere: readonly string[] }, who: Who): ItemContext {
  const by = n.suspendedBy ? asPerson(who, n.suspendedBy) : null;
  return {
    subject: { kind: 'department', title: n.displayName },
    summary: n.stopped
      ? `${n.displayName}’s agents are stopped${by ? ` by ${by.name}` : ''}${n.reason ? `: “${clean(n.reason)}”` : ''}. Nothing is deleted; they simply cannot act.`
      : n.agentsHere.length === 0
        ? `${n.displayName} has no agent working, so its lead does the work and there is nothing to stop.`
        : `${listWords(n.agentsHere)} ${n.agentsHere.length === 1 ? 'is' : 'are'} working in ${n.displayName}.`,
    why: 'A department can be stopped on its own so that every other one keeps running.',
    source: n.stopped ? { label: 'A superadmin stopped it' } : null,
    broughtBy: by ? { ...by, role: 'stopped it' } : null,
    owner: who.lead(n.name),
    involved: who.agentsOf(n.name),
    knows: who.knowers(n.name),
    next: n.stopped
      ? { tip: 'Start its agents again from this row once the reason it was stopped is settled.', instead: 'Leaving it stopped affects only this department.' }
      : n.agentsHere.length === 0
        ? { tip: 'Nothing to do here.', instead: 'Agents and mailboxes are declared in the ledger; a lead who wants one asks for it.' }
        : { tip: 'Nothing is needed. Stop its agents from this row only if something in this department must pause while the rest keeps running.', instead: 'Stopping and starting again each need a single-use code.' },
  };
}

// ---------------------------------------------------------------------------
// A twin's task, and the tool it waits on
// ---------------------------------------------------------------------------

const twinParty = (twin: string | null, principal: string | null): Party => ({ name: twin ? (principal ? twinLabel(twin, principal) : `${twin} · a twin`) : 'A twin', kind: 'twin' });

export function explainPendingTaskTool(p: WirePendingTaskTool, who: Who, earnedAfter: number, now = Date.now()): ItemContext {
  const name = toolName(p.toolId);
  const twin = twinParty(p.twin, p.principal);
  const person = p.principal ?? 'its person';
  const waited = waitedWords(p.requestedAt, now);
  const unavailable = p.state === 'unavailable';
  const rec = (p.record as WirePendingTaskTool['record'] | undefined) ?? { clean: 0, stopped: false };
  const record = rec.stopped
    ? `Granting it by itself is stopped for it in ${who.label(p.department)}.`
    : `It has been granted ${rec.clean} of ${earnedAfter} times in a row without a refusal in ${who.label(p.department)}; at ${earnedAfter} the platform would grant it by itself.`;
  return {
    subject: { kind: 'tool request', title: `${name} for ${twin.name}` },
    summary: `${twin.name} has a task that needs ${name}, which it does not hold. A twin runs only the plan its person confirmed, so the task waits.`,
    need: unavailable ? 'The tool set up first; it cannot be granted until it is signed and admitted.' : 'Grant it to this one task for as long as you say, or refuse with a reason that goes back to its person.',
    why: `${p.need === 'found_while_running' ? 'The task’s plan did not name this tool; the twin found it needed it while running.' : 'The confirmed plan named this tool.'} It has waited ${waited}.`,
    source: { label: `${twin.name}’s confirmed task, stopped at a tool it lacks`, when: dayWords(p.requestedAt) },
    broughtBy: { ...twin, when: dayWords(p.requestedAt) },
    owner: { ...who.me, role: 'grants or refuses it' },
    involved: [twin],
    knows: [...(p.principal ? [{ name: p.principal, kind: 'person' as const, role: 'the task’s person' }] : []), ...who.knowers(p.department)].slice(0, 4),
    facts: [
      ['Department', who.label(p.department)],
      ['Its record', record],
    ],
    next: unavailable
      ? { tip: 'Set the tool up first: the line above says what is missing. Then grant it to the task.', instead: 'Refusing hands the task back to its person with your reason, if the tool is not going to exist.' }
      : {
          tip: `Check the tool fits what ${person} asked for, then grant it for the shortest time that will do. Granting spends a single-use code bound to this task and this tool.`,
          instead: `Refusing needs no code and hands the task back to ${person} with your reason; it also resets the tool’s record toward being granted by itself.`,
        },
  };
}

export function explainLiveTaskGrant(g: WireLiveTaskGrant, who: Who): ItemContext {
  const name = toolName(g.toolId);
  const twin = twinParty(g.twin, null);
  return {
    subject: { kind: 'tool grant', title: `${name} for ${twin.name}` },
    summary: `${name} is lent to ${g.twin ?? 'a twin'}’s task in ${who.label(g.department)} until ${dayWords(g.validTo)}, and to nothing else.`,
    why: 'A tool reaches a twin’s task only as a grant to that one task, so every live grant is listed for a superadmin to end early.',
    source: { label: g.basis === 'earned' ? 'Granted by the platform, on the tool’s record of unbroken grants' : 'Granted by a superadmin' },
    broughtBy: g.basis === 'earned' ? platform() : null,
    owner: { ...who.me, role: 'may end it' },
    involved: [twin],
    knows: who.knowers(g.department),
    next: {
      tip: 'Nothing is needed. It ends by itself on that date, or sooner if the task ends.',
      instead: 'To stop it now use “End it”: the task’s next call is refused. Ending needs no code, because it narrows.',
    },
  };
}

export function explainTaskGrantCount(c: WireTaskGrantCount, who: Who, earnedAfter: number): ItemContext {
  const name = toolName(c.toolId);
  const dept = who.label(c.department);
  // Some replies carry the counts without the record; then nothing is claimed of it.
  const rec = (c.record as WireTaskGrantCount['record'] | undefined) ?? { clean: 0, stopped: false, earned: false };
  const stopped = rec.stopped;
  const next: ItemContext['next'] = stopped
    ? { tip: `Granting ${name} by itself is stopped in ${dept}, so each grant is yours. Start it again only once you trust the record; that spends a single-use code.`, instead: 'Leaving it stopped is safe: it only means each request comes to you.' }
    : rec.earned
      ? { tip: `The platform now grants ${name} in ${dept} by itself, for at most fourteen days, and receipts every grant. Nothing is needed unless you want each one to be yours again.`, instead: 'Stop it with “Stop”; that is at once and needs no code.' }
      : { tip: `${rec.clean} of ${earnedAfter} grants in a row have gone without a refusal. At ${earnedAfter} the platform could grant it by itself once a superadmin starts that; nothing is needed yet.`, instead: 'A single refusal sends the count back to zero.' };
  return {
    subject: { kind: 'tool record', title: `${name} in ${dept}` },
    summary: `In the last ninety days ${name} was granted to twins’ tasks in ${dept} ${plural(c.supervised, 'time')} by a superadmin and ${plural(c.earned, 'time')} on its record, and refused ${plural(c.refused, 'time')}.`,
    why: 'The platform counts every grant by who made it, because five unbroken grants earn the next one without a press.',
    source: { label: 'Counted from the grants and refusals of the last ninety days' },
    broughtBy: platform(),
    owner: { ...who.me, role: 'decides whether it is granted by itself' },
    knows: who.knowers(c.department),
    next,
  };
}

export function explainNotifyChannel(c: WireNotifyChannel, who?: Who): ItemContext {
  const adder = who && c.addedBy ? who.actor(c.addedBy) : null;
  const kind = c.kind === 'google_chat' ? 'Google Chat' : 'Telegram';
  return {
    subject: { kind: 'notice place', title: c.label },
    summary: `Notices that a twin’s task waits on a tool are also sent to ${c.label}, on ${kind}.`,
    why: 'A task that waits is said in the header and the side chat; this is one more place so that nobody has to be looking.',
    source: { label: `Added to the places notices go`, when: dayWords(c.addedAt) },
    // The superadmin who added it (0162); an older reply without the field reads as not recorded.
    broughtBy: adder ? { ...adder, role: 'added it', when: dayWords(c.addedAt) } : null,
    next: {
      tip: 'Nothing is needed. Each notice is one line naming the twin, its department and the tool it waits on, with a link back here, and never what the task is about.',
      instead: 'If it should stop getting them, use “Remove”; nothing else changes.',
    },
  };
}

// ---------------------------------------------------------------------------
// Hosted assistants
// ---------------------------------------------------------------------------

export function explainHostedVendor(v: { name: string }, current: WireHostedClient | undefined, who: Who): ItemContext {
  const by = current ? asPerson(who, current.admittedBy) : null;
  return {
    subject: { kind: 'assistant', title: v.name },
    summary: current
      ? `${v.name} is admitted: it may sign in as a person over the platform’s assistant door, only with each person’s own approval. ${plural(current.liveGrants, 'person', 'people')} ${current.liveGrants === 1 ? 'is' : 'are'} signed in now.`
      : `${v.name} is not admitted, so it cannot sign in as anyone.`,
    need: current ? undefined : 'A superadmin’s admission, once the vendor’s paperwork is marked on the Workspace.',
    why: 'A vendor’s assistant is the same decision as a vendor’s tool, so it sits beside the tool library.',
    source: current ? { label: 'Admitted by a superadmin', when: dayWords(current.admittedAt) } : null,
    broughtBy: by && current ? { ...by, role: 'admitted it', when: dayWords(current.admittedAt) } : null,
    owner: { ...who.me, role: 'admits or withdraws it' },
    facts: current ? [['Signed in now', String(current.liveGrants)], ['Registrations', String(current.registrations)]] : [],
    next: current
      ? { tip: 'Nothing is needed. It reaches only what a person’s own read models show and never sends.', instead: 'To stop it, withdraw it; that needs no code and ends every connection at once.' }
      : { tip: `Admit ${v.name} only after its paperwork is marked on the Workspace, and with the exact redirect addresses its vendor publishes. Admitting spends a single-use code.`, instead: 'Leaving it unadmitted is safe: nothing can sign in as a person through it.' },
  };
}
